import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Turnstile from "@/components/Turnstile";
import { supabase } from "@/integrations/supabase/client";
import { verifyTurnstile } from "@/lib/verifyTurnstile";
import { Mail, ArrowRight } from "lucide-react";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const ok = await verifyTurnstile(captchaToken, "password_reset");
    if (!ok) {
      setLoading(false);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      toast({
        title: "Verification failed",
        description: "Please complete the security check and try again.",
        variant: "destructive",
      });
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      toast({ title: "Could not send reset email", description: error.message, variant: "destructive" });
      return;
    }
    setSent(true);
    toast({ title: "Check your email", description: "We've sent you a password reset link." });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container flex items-center justify-center py-16 md:py-24">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8 shadow-[var(--card-shadow)]">
            <div className="text-center mb-8">
              <img src="/logo.png" alt="Vecurate" className="w-20 h-20 object-contain mx-auto mb-4" />
              <h1 className="font-serif text-2xl text-foreground">Forgot Password</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {sent ? "Check your inbox for the reset link." : "Enter your email and we'll send you a reset link."}
              </p>
            </div>
            {!sent && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10" placeholder="you@example.com" />
                  </div>
                </div>
                <div className="flex justify-center">
                  <Turnstile key={captchaKey} action="password_reset" onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} onError={() => setCaptchaToken(null)} />
                </div>
                <Button type="submit" className="w-full gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading || !captchaToken}>
                  {loading ? "Verifying..." : "Send Reset Link"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            )}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Remembered it?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  Back to sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPasswordPage;
